import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Music2, Mail, Phone, Award, Clock, GraduationCap,
  Sparkles, Link as LinkIcon, Truck, Banknote, MessageCircle, Star,
} from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { localYmd } from "../lib/utils";
import { BookingModal } from "./Search";

const LEVEL_LABEL = {
  beginner: "Beginner / improver",
  intermediate: "Intermediate (Grade 5+)",
  advanced: "Advanced (Grade 8 standard)",
  grade_8_plus: "Grade 8 distinction or beyond",
  diploma_conservatoire: "Diploma / conservatoire-trained",
  professional_standard: "Professional standard",
};

const FEE_LABEL = {
  paid: "Paid only",
  unpaid: "Happy to play unpaid",
  expenses: "Expenses covered",
  any: "Any (paid / unpaid / expenses)",
};

const CONTACT_LABEL = {
  in_app: "via AMDS messages",
  email: "by email",
  phone: "by phone",
};

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="label-overline mb-2 inline-flex items-center gap-2">
        <Icon size={12} /> {title}
      </div>
      <div className="text-ink-700 leading-relaxed">{children}</div>
    </section>
  );
}

function autoLink(text) {
  if (!text) return null;
  const parts = text.split(/(\s+)/);
  return parts.map((seg, i) => {
    if (/^https?:\/\/\S+/.test(seg)) {
      return (
        <a key={i} href={seg} target="_blank" rel="noreferrer" className="text-sage-600 underline break-words inline-flex items-center gap-1">
          <LinkIcon size={12} /> {seg.replace(/^https?:\/\//, "")}
        </a>
      );
    }
    return seg;
  });
}

export default function MusicianView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [book, setBook] = useState(false);
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    let blobUrl = null;
    api.get(`/musicians/${id}`).then(async ({ data }) => {
      setP(data);
      if (data.avatar_path) {
        try {
          const res = await fetch(`${API}/files/${data.avatar_path}`, { credentials: "include" });
          if (res.ok) {
            blobUrl = URL.createObjectURL(await res.blob());
            setAvatar(blobUrl);
          }
        } catch {}
      }
    }).catch(() => setP(false));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [id]);

  if (p === null) return <div className="text-ink-500">Loading…</div>;
  if (p === false) return <div className="card-base p-6 text-ink-600">Profile not found.</div>;

  const isConductor = p.role === "conductor";
  const has = (s) => typeof s === "string" ? s.trim().length > 0 : !!s;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="musician-view">
      <Link to="/search" className="btn-ghost text-sm w-fit"><ArrowLeft size={16} /> Back to search</Link>

      {/* Hero */}
      <div className="card-base p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-2xl bg-sage-100 text-sage-600 grid place-items-center font-heading text-2xl font-semibold overflow-hidden shrink-0">
            {avatar ? <img src={avatar} alt={p.name} className="w-full h-full object-cover" /> : (p.name?.[0] || "?")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline">{isConductor ? "Conductor" : "Musician"}</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">
              {p.name}
              {p.founding_member && (
                <span className="ml-2 inline-flex items-center gap-1 align-middle pill bg-terracotta-500 text-white text-xs" data-testid="founding-member-badge" title="AMDS Founding Member — invited 3+ friends during beta">
                  <Star size={12} className="fill-white" /> Founding Member
                </span>
              )}
            </h1>
            <div className="text-ink-700 text-base mt-1 inline-flex items-center gap-2 flex-wrap">
              <Music2 size={16} className="text-sage-500" />
              <span className="font-medium">{p.instrument || (isConductor ? "Conductor" : "Instrument not set")}</span>
              {p.voice_type && <span className="text-ink-500 text-sm">· {p.voice_type}</span>}
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-ink-500 mt-2">
              {has(p.location) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} /> {p.location}{!isConductor ? ` · up to ${p.travel_radius_km}km` : ""}
                </span>
              )}
              {p.years_playing > 0 && (
                <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {p.years_playing} year{p.years_playing === 1 ? "" : "s"} playing</span>
              )}
              {has(p.playing_level) && (
                <span className="pill bg-sage-100 text-sage-700"><Award size={12} /> {LEVEL_LABEL[p.playing_level] || p.playing_level}</span>
              )}
            </div>
          </div>
        </div>

        {p.secondary_instruments?.length > 0 && (
          <div className="mt-5">
            <div className="label-overline mb-2">Also plays</div>
            <div className="flex flex-wrap gap-2">
              {p.secondary_instruments.map((s) => <span key={s} className="pill bg-ivory-100 text-ink-700">{s}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Biography card */}
      {(has(p.bio) || has(p.training) || has(p.ensemble_experience) || has(p.notable_performances)
        || (isConductor && (has(p.ensembles_led) || has(p.repertoire_highlights)))
        || has(p.links)) && (
        <div className="card-base p-6 sm:p-8 space-y-5">
          {has(p.bio) && (
            <Section title="About" icon={Sparkles}>
              <p className="whitespace-pre-line">{p.bio}</p>
            </Section>
          )}
          {has(p.training) && (
            <Section title="Training" icon={GraduationCap}>
              <p>{p.training}</p>
            </Section>
          )}
          {has(p.ensemble_experience) && (
            <Section title="Ensemble experience" icon={Music2}>
              <p className="whitespace-pre-line">{p.ensemble_experience}</p>
            </Section>
          )}
          {has(p.notable_performances) && (
            <Section title="Notable performances" icon={Award}>
              <p className="whitespace-pre-line">{p.notable_performances}</p>
            </Section>
          )}
          {isConductor && has(p.ensembles_led) && (
            <Section title="Ensembles led" icon={Music2}>
              <p className="whitespace-pre-line">{p.ensembles_led}</p>
            </Section>
          )}
          {isConductor && has(p.repertoire_highlights) && (
            <Section title="Repertoire highlights" icon={Award}>
              <p className="whitespace-pre-line">{p.repertoire_highlights}</p>
            </Section>
          )}
          {has(p.links) && (
            <Section title="Recordings & links" icon={LinkIcon}>
              <p className="text-sm break-words">{autoLink(p.links)}</p>
            </Section>
          )}
        </div>
      )}

      {/* Practical info */}
      {!isConductor && (
        <div className="card-base p-6">
          <div className="label-overline mb-3">Practical</div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-ivory-100">
              <Banknote size={16} className="text-sage-600" />
              <div>
                <div className="text-ink-500 text-xs">Fee preference</div>
                <div className="text-ink-800 font-medium">{FEE_LABEL[p.fee_preference] || p.fee_preference}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-ivory-100">
              <Truck size={16} className="text-sage-600" />
              <div>
                <div className="text-ink-500 text-xs">Own transport</div>
                <div className="text-ink-800 font-medium">{p.own_transport ? "Yes" : "No"}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-ivory-100">
              <MessageCircle size={16} className="text-sage-600" />
              <div>
                <div className="text-ink-500 text-xs">Prefers contact</div>
                <div className="text-ink-800 font-medium">{CONTACT_LABEL[p.contact_pref] || p.contact_pref}</div>
              </div>
            </div>
            {p.contact_pref === "email" && p.email && (
              <a href={`mailto:${p.email}`} className="flex items-center gap-2 p-3 rounded-xl bg-ivory-100 hover:bg-ivory-200 transition">
                <Mail size={16} className="text-sage-600" />
                <div>
                  <div className="text-ink-500 text-xs">Email</div>
                  <div className="text-ink-800 font-medium break-all">{p.email}</div>
                </div>
              </a>
            )}
            {p.contact_pref === "phone" && p.phone && (
              <a href={`tel:${p.phone}`} className="flex items-center gap-2 p-3 rounded-xl bg-ivory-100 hover:bg-ivory-200 transition">
                <Phone size={16} className="text-sage-600" />
                <div>
                  <div className="text-ink-500 text-xs">Phone</div>
                  <div className="text-ink-800 font-medium">{p.phone}</div>
                </div>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Empty-profile guidance */}
      {!has(p.bio) && !has(p.training) && !has(p.ensemble_experience) && !has(p.notable_performances) && (
        <div className="card-base p-5 text-sm text-ink-500" data-testid="empty-bio-note">
          {p.name?.split(" ")[0] || "This player"} hasn't filled in their bio yet — just the basics. You can still send a request and ask for details in the message thread.
        </div>
      )}

      {/* CTA */}
      {user?.role !== "musician" && !isConductor && (
        <div className="sticky bottom-[140px] sm:static z-30">
          <button onClick={() => setBook(true)} className="btn-primary w-full sm:w-auto justify-center" data-testid="profile-book-btn">
            Send booking request
          </button>
        </div>
      )}

      {book && <BookingModal musician={{ user_id: id, name: p.name }} initialDate={localYmd()} onClose={() => setBook(false)} />}
    </div>
  );
}
